/**
 * "Deliver Story" on the new architecture: a distribution with no split field, so everyone on it
 * gets the whole Story at their own clearance (IMP-291, IMP-293). Send now and Schedule write the
 * distribution step by step, each step waiting for Story Manager to apply it, then activate it;
 * Send now also starts a run. The Active deliveries list reads these distributions back.
 */
import type { Delivery, DeliveryStatus, Platform, SendNowFormState, ScheduleConfig } from "../../components/organisms/DeliverStory/types";
import { fromApiWeekday, toApiWeekday } from "../distributions/distributionScheduleMappers";
import { zonedIsoDate } from "../distributions/distributionTime";
import {
  createDistribution,
  deleteDistribution,
  patchDistribution,
  putDistributionSchedule,
  putRecipients,
  setDistributionStatus,
  startDistributionRun,
} from "../distributions/distributionsApi";
import { waitForDistributionEdit } from "../distributions/distributionEdits";
import {
  DEFAULT_AUDIENCE,
  type ChannelApi,
  type DistributionApi,
  type DistributionContentApi,
  type DistributionScheduleApi,
  type DistributionSummaryApi,
  type RecipientApi,
  type RecipientsPutApi,
  type WriteAcceptedApi,
} from "../distributions/distributions.types";

const NAME_MAX = 120;

const pad = (n: number): string => String(n).padStart(2, "0");

const stamp = (at: Date): string =>
  `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(at.getHours())}:${pad(at.getMinutes())}`;

/** "Send now · email · 2026-10-31 14:05": how the distribution reads on the Distributions page. */
export const deliverStoryName = (form: Pick<SendNowFormState, "whenToSend" | "platform">, at: Date = new Date()): string =>
  `${form.whenToSend === "schedule" ? "Scheduled delivery" : "Send now"} · ${form.platform} · ${stamp(at)}`.slice(0, NAME_MAX);

/** The memo is its own template; the Story and the presentation draw the Story deck. */
export const deliverStoryContent = (form: Pick<SendNowFormState, "artifact" | "format" | "message" | "audience">): DistributionContentApi => {
  const memo = form.artifact === "memo";
  return {
    template_id: memo ? "memo" : "story",
    theme_id: null,
    formats: !memo && form.format === "presentation" ? ["pptx"] : ["pdf"],
    include: { files: true, link: false, message: true },
    include_ai_summary: false,
    include_snapshots: false,
    audience: form.audience ?? DEFAULT_AUDIENCE,
    branding: { name: null, logo_url: null, accent: null, mark: null },
    message: { email_subject: "{{story}}", email_body: form.message, slack_text: form.message },
  };
};

/** Each destination is one recipient, with no value: there is no split field. */
export const deliverStoryRecipients = (form: Pick<SendNowFormState, "platform" | "destinations">): RecipientsPutApi => ({
  source: { kind: "list", file_name: null, matched_columns: {} },
  rows: form.destinations.map((address, index) => ({
    position: index + 1,
    name: address,
    channel: form.platform as ChannelApi,
    address,
    cc: [],
    formats: null,
    template_id: null,
    theme_id: null,
    brand: null,
    extra: {},
    source: "manual",
  })),
});

const ENDS: Record<ScheduleConfig["endsMode"], (s: ScheduleConfig) => DistributionScheduleApi["ends"]> = {
  never: () => ({ kind: "never" }),
  on: (s) => ({ kind: "on", date: s.endsOn }),
  after: (s) => ({ kind: "after", runs: s.endsAfter ?? 1 }),
};

/** The form's recurrence as the distribution schedule: a monthly one sends on its start date's day. */
export const deliverStorySchedule = (s: ScheduleConfig): DistributionScheduleApi => ({
  kind: s.unit === "day" ? "daily" : s.unit === "week" ? "weekly" : "monthly",
  every: s.every,
  at: `${pad(s.refreshHour ?? 0)}:00`,
  timezone: s.timezone || "UTC",
  weekdays: s.unit === "week" ? s.repeatOn.map(toApiWeekday).sort((a, b) => a - b) : [],
  day_of_month: s.unit === "month" ? Number(s.startsOn.slice(8, 10)) || 1 : null,
  monthly_rule: null,
  quarter_month: null,
  starts_on: s.startsOn,
  ends: ENDS[s.endsMode](s),
});

const PLATFORMS = new Set<ChannelApi>(["email", "slack", "teams"]);

const UNITS: Partial<Record<DistributionScheduleApi["kind"], ScheduleConfig["unit"]>> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
};

/** A schedule the Deliver Story form can show: daily, weekly, or monthly on a numbered day. */
export const fitsDeliverStoryForm = (s: DistributionScheduleApi | null): s is DistributionScheduleApi =>
  s !== null && UNITS[s.kind] !== undefined && !s.monthly_rule && s.day_of_month !== "last";

/**
 * The saved schedule back in the form. A start date already past moves to the next send's date,
 * which keeps the same weekday or day of the month.
 */
export const deliverStoryScheduleForm = (s: DistributionScheduleApi, nextRunAt: string | null, today: string): ScheduleConfig => {
  const nextSendDay = nextRunAt ? zonedIsoDate(Date.parse(nextRunAt), s.timezone) : today;
  return {
    every: s.every,
    unit: UNITS[s.kind] ?? "week",
    repeatOn: s.kind === "weekly" ? s.weekdays.map(fromApiWeekday) : [],
    refreshHour: Number(s.at.slice(0, 2)),
    timezone: s.timezone,
    startsOn: s.starts_on >= today ? s.starts_on : nextSendDay < today ? today : nextSendDay,
    endsMode: s.ends.kind,
    endsOn: s.ends.kind === "on" ? s.ends.date : "",
    endsAfter: s.ends.kind === "after" ? s.ends.runs : null,
  };
};

/** A scheduled Deliver Story distribution as the form that made it. */
export const deliverStoryForm = (
  d: DistributionApi,
  recipients: readonly RecipientApi[],
  prev: SendNowFormState,
  today: string,
): SendNowFormState => ({
  ...prev,
  platform: (recipients[0]?.channel ?? d.channels.find((c) => PLATFORMS.has(c)) ?? prev.platform) as Platform,
  destinations: recipients.map((r) => r.address),
  whenToSend: "schedule",
  artifact: d.content.template_id === "memo" ? "memo" : "story",
  format: d.content.formats.includes("pptx") ? "presentation" : "pdf",
  audience: d.content.audience === "inside" ? "inside" : "outside",
  message: d.content.message.email_body,
  schedule: d.schedule ? deliverStoryScheduleForm(d.schedule, d.next_run_at, today) : prev.schedule,
});

const applied = async (storyId: string, write: Promise<WriteAcceptedApi>): Promise<string> => {
  const accepted = await write;
  await waitForDistributionEdit(storyId, accepted.request_id);
  return accepted.distribution_id;
};

/**
 * Creates and activates the distribution, then sends it now when there is no schedule. A step
 * that fails rejects with Story Manager's own message, and the half-made draft is removed.
 */
export const deliverStory = async (storyId: string, form: SendNowFormState, at: Date = new Date()): Promise<string> => {
  const distributionId = await applied(storyId, createDistribution(storyId, { name: deliverStoryName(form, at) }));
  try {
    await applied(storyId, putRecipients(storyId, distributionId, deliverStoryRecipients(form)));
    await applied(storyId, patchDistribution(storyId, distributionId, { content: deliverStoryContent(form) }));
    if (form.whenToSend === "schedule") {
      await applied(storyId, putDistributionSchedule(storyId, distributionId, deliverStorySchedule(form.schedule)));
    }
    await applied(storyId, setDistributionStatus(storyId, distributionId, { status: "active" }));
    if (form.whenToSend !== "schedule") {
      await startDistributionRun(storyId, distributionId, { trigger: "now" });
    }
  } catch (error) {
    await deleteDistribution(storyId, distributionId).catch(() => undefined);
    throw error;
  }
  return distributionId;
};

/** Saves an edited schedule: its recipients, its content and its schedule, each applied in turn. */
export const updateDeliverStory = async (storyId: string, distributionId: string, form: SendNowFormState): Promise<void> => {
  await applied(storyId, putRecipients(storyId, distributionId, deliverStoryRecipients(form)));
  await applied(storyId, patchDistribution(storyId, distributionId, { content: deliverStoryContent(form) }));
  await applied(storyId, putDistributionSchedule(storyId, distributionId, deliverStorySchedule(form.schedule)));
};

/** A send now has no schedule: once it has run, it reads as completed. */
const statusOf = (d: DistributionSummaryApi): DeliveryStatus | null => {
  if (d.status === "paused" || d.status === "completed") return d.status;
  if (d.status !== "active") return null;
  if (d.schedule) return "active";
  return d.last_run ? "completed" : null;
};

/** How many of the Story's distributions will send again on a schedule; a done Send now is not one. */
export const countScheduled = (items: readonly DistributionSummaryApi[]): number =>
  items.filter((d) => d.status === "active" && d.schedule).length;

/** The Story's "Deliver Story" distributions (no split field) as the Active deliveries rows. */
export const toDeliveryRows = (items: readonly DistributionSummaryApi[]): Delivery[] =>
  items.flatMap((d) => {
    const status = statusOf(d);
    const platform = d.channels.find((c) => PLATFORMS.has(c));
    if (d.split_label !== null || status === null || !platform) return [];
    const recipients = `${d.recipient_count} recipient${d.recipient_count === 1 ? "" : "s"}`;
    return [
      {
        id: d.id,
        platform: platform as Delivery["platform"],
        destinations: [recipients],
        deliveryType: d.schedule ? "scheduled" : "immediate",
        format: d.formats.includes("pptx") ? "presentation" : "pdf",
        status,
        editable: fitsDeliverStoryForm(d.schedule),
      },
    ];
  });
